import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { LucideDynamicIcon } from '@lucide/angular';
import { I18nService } from '../../../core/services/i18n.service';

/**
 * LE tiroir latéral du SI — un seul, pour tous les panneaux qui glissent depuis le bord.
 *
 * <h2>Pourquoi un composant partagé</h2>
 * Le tiroir des notifications était écrit à même le layout, et la messagerie de mission en avait
 * réécrit un second à la main : autre largeur, en-tête navy au lieu du papier, voile sans flou, et
 * surtout ni Échap ni piège de focus (revue UX du 2026-09-22). Deux tiroirs qui se ressemblent sans
 * se comporter pareil, c'est pire qu'un seul : l'agent apprend un geste qui ne marche qu'à moitié.
 *
 * <h2>Popover API native</h2>
 * `popover="auto"` apporte gratuitement ce qu'un tiroir fait à la main de travers : couche
 * supérieure (aucun z-index à négocier), Échap, clic hors du panneau, et un seul tiroir ouvert à la
 * fois. Le piège de focus (CDK) et le RETOUR du focus sur le déclencheur complètent le rôle
 * `dialog` — le Popover ne les fait pas.
 *
 * <h2>Deux façons de piloter</h2>
 * - déclarative : un bouton `popovertarget="<panelId>"` (la cloche de la barre du haut) ;
 * - par l'état : `[(open)]` ou `show()` / `hide()` / `toggle()` (la messagerie de mission).
 * L'état suit le panneau dans les deux cas : `open` repasse à faux quand Échap ou un clic extérieur
 * le referment.
 *
 * <h2>Emplacements</h2>
 * `[slideOverTitleExtra]` (à côté du titre) · `[slideOverActions]` (barre sous l'en-tête) · corps
 * par défaut (seule zone qui défile) · `[slideOverFooter]` (pied collé en bas).
 */
@Component({
  selector: 'app-slide-over',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkTrapFocus, LucideDynamicIcon],
  templateUrl: './slide-over.html',
  styleUrl: './slide-over.css',
})
export class SlideOver {
  private readonly i18n = inject(I18nService);

  /** L'`id` du panneau — la cible d'un `popovertarget`. */
  readonly panelId = input<string | null>(null);
  readonly titre = input.required<string>();
  readonly sousTitre = input<string | null>(null);
  /** Largeur du panneau (px) ; bornée par la largeur de l'écran. */
  readonly largeur = input(420);
  /** Classe supplémentaire sur le panneau — pour les sélecteurs de recette qui le désignent. */
  readonly panelClass = input('');
  /** Le corps défile-t-il avec un fond papier et des marges (liste) ou nu (fil de messages) ? */
  readonly corpsNu = input(false);

  /** Ouvert / fermé — suit aussi les fermetures natives (Échap, clic extérieur). */
  readonly open = model(false);

  private readonly panel = viewChild.required<ElementRef<HTMLElement>>('panel');
  /** L'état RÉEL du popover, qui pilote le piège de focus. */
  readonly ouvertReel = signal(false);
  private declencheur: HTMLElement | null = null;

  readonly libelleFermer = computed(() => (this.i18n.lang() === 'ar' ? 'إغلاق' : 'Fermer'));

  constructor() {
    // L'état voulu suit l'état natif. Un tiroir peut NAÎTRE ouvert (`[open]="true"` sous un `@if`,
    // le panneau d'un constat) : le premier passage attend donc que le panneau soit dans le document.
    // Un tiroir retiré du DOM encore ouvert (son `@if` tombe) ne reçoit pas de `toggle` : le
    // focus serait perdu sur <body>. On le rend ici au déclencheur.
    inject(DestroyRef).onDestroy(() => {
      const d = this.declencheur;
      if (d?.isConnected) {
        queueMicrotask(() => d.focus());
      }
    });
    let pret = false;
    afterNextRender(() => {
      pret = true;
      this.synchroniser(this.open());
    });
    effect(() => {
      const voulu = this.open();
      if (pret) {
        this.synchroniser(voulu);
      }
    });
  }

  private synchroniser(voulu: boolean): void {
    const el = this.panel().nativeElement as HTMLElement & {
      showPopover?: () => void;
      hidePopover?: () => void;
    };
    if (!el.isConnected) {
      return;
    }
    const estOuvert = el.matches(':popover-open');
    try {
      if (voulu && !estOuvert) {
        el.showPopover?.();
      } else if (!voulu && estOuvert) {
        el.hidePopover?.();
      }
    } catch {
      // Un autre popover « auto » vient de se fermer au même instant : l'état natif fait foi, et
      // `toggle` réalignera `open`.
    }
  }

  show(): void {
    this.open.set(true);
  }

  hide(): void {
    this.open.set(false);
  }

  toggle(): void {
    this.open.update((v) => !v);
  }

  /**
   * Un clic sur le VOILE ferme le tiroir. Le `::backdrop` appartient au panneau : le clic y arrive
   * avec le panneau pour cible, que le Popover tient donc pour un clic « dedans » et ne ferme pas.
   */
  onPanelClick(e: MouseEvent): void {
    if (e.target === this.panel().nativeElement) {
      const r = this.panel().nativeElement.getBoundingClientRect();
      const dehors = e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
      if (dehors) {
        this.hide();
      }
    }
  }

  /** `beforetoggle` : on retient qui avait le focus AVANT que le panneau ne le prenne. */
  onBeforeToggle(e: Event): void {
    if ((e as ToggleEvent).newState === 'open') {
      const actif = document.activeElement;
      this.declencheur = actif instanceof HTMLElement ? actif : null;
    }
  }

  /** `toggle` : l'état natif fait foi — Échap et clic extérieur passent par ici. */
  onToggle(e: Event): void {
    const ouvert = (e as ToggleEvent).newState === 'open';
    this.ouvertReel.set(ouvert);
    if (this.open() !== ouvert) {
      this.open.set(ouvert);
    }
    if (ouvert) {
      // Le champ marqué `data-autofocus` d'abord (la saisie d'un message), sinon le bouton fermer.
      const cible =
        this.panel().nativeElement.querySelector<HTMLElement>('[data-autofocus]') ??
        this.panel().nativeElement.querySelector<HTMLElement>('.so-close');
      queueMicrotask(() => cible?.focus());
    } else {
      const d = this.declencheur;
      this.declencheur = null;
      if (d?.isConnected) {
        d.focus();
      }
    }
  }
}
